import type { Reducer, Dispatch } from 'react';
import { useCallback, useEffect, useReducer, useRef } from 'react';

import { EMPTY_ARRAY } from '@e-socna/empty';

export const useReducerWithMiddleware = <S, A>(
  reducer: Reducer<S, A>,
  initialState: S,
  middlewareFns: Readonly<
    readonly ((state: S, action?: A | null) => unknown)[]
  > = EMPTY_ARRAY,
  afterwareFns: Readonly<
    readonly ((state: S, dispatch: Dispatch<A>, action?: A | null) => unknown)[]
  > = EMPTY_ARRAY,
  // eslint-disable-next-line max-params
) => {
  const [state, dispatch] = useReducer(reducer, initialState);

  const aRef = useRef<A | null>(null);

  const dispatchWithMiddleware = useCallback(
    (action: A) => {
      middlewareFns.forEach((middlewareFn) => middlewareFn(state, action));

      aRef.current = action;

      dispatch(action);
    },
    [middlewareFns, state],
  );

  useEffect(() => {
    if (!aRef.current) {
      return;
    }

    afterwareFns.forEach((afterwareFn) =>
      afterwareFn(state, dispatch, aRef.current),
    );

    aRef.current = null;
  }, [afterwareFns, state]);

  return [state, dispatchWithMiddleware] as const;
};
